import type { ButtonHTMLAttributes } from "react";
import { forwardRef } from "react";

type Variant = "primary" | "secondary" | "ghost" | "ghost-danger" | "danger";
type Size = "sm" | "md";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
}

const baseClasses =
  "inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500 " +
  "focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-950 " +
  "disabled:pointer-events-none disabled:opacity-50";

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 text-white shadow-glow hover:brightness-110 active:brightness-95",
  secondary:
    "border bg-white text-zinc-800 border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 active:bg-zinc-100 " +
    "dark:bg-zinc-800/80 dark:text-zinc-100 dark:border-zinc-700/70 dark:hover:bg-zinc-800 dark:hover:border-zinc-600 dark:active:bg-zinc-800/60",
  ghost:
    "bg-transparent text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 " +
    "dark:text-zinc-300 dark:hover:bg-zinc-800/70 dark:hover:text-zinc-50",
  "ghost-danger":
    "bg-transparent text-red-600 hover:bg-red-50 hover:text-red-700 " +
    "dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300",
  danger:
    "bg-red-600 text-white hover:bg-red-500 active:bg-red-700 " +
    "dark:bg-red-500/90 dark:hover:bg-red-500 dark:active:bg-red-600",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-8 px-3 text-[13px]",
  md: "h-9 px-4 text-[13.5px]",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "primary", size = "md", block = false, className, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={[
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        block ? "w-full" : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    />
  );
});
